import React, { useState, useEffect, forwardRef } from 'react';
import { taskService, scheduleService } from '../services';
import { Checkbox, Select, formatSelectOptions, Input, TextareaAutosize } from '../components';
import {
  getBatchEditValue,
  cronDescription,
  getTimezoneOptions,
  getTimezoneAbbreviation,
  getUserTimezone,
} from '../helpers';
import { useMessage } from '../hooks';

// Computed once at module load — building the ~600-item list is a one-time O(n) cost.
const timezoneOptions = getTimezoneOptions();

const _AddScheduleForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    schedule_id: null,
    schedule_name: '',
    schedule_description: '',
    schedule_cron: '',
    schedule_timezone: getUserTimezone(),
    schedule_is_enabled: true,
    task_ids: [],
  };

  const initialBatchEditItems = {
    schedule_is_enabled: false,
    schedule_timezone: false,
  };

  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [tasks, setTasks] = useState([]);
  const [cronDescriptionText, setCronDescription] = useState('');

  const { showMessage, hideMessage } = useMessage();

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        schedule_id: data[0].schedule_id,
        schedule_name: data[0].schedule_name || '',
        schedule_description: data[0].schedule_description || '',
        schedule_cron: data[0].schedule_cron || '',
        schedule_timezone: data[0].schedule_timezone || getUserTimezone(),
        schedule_is_enabled:
          data[0].schedule_is_enabled !== undefined ? data[0].schedule_is_enabled : true,
        task_ids: [],
      });
      fetchAssignedTasks(data[0].schedule_id);
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);
      setFormData({
        schedule_is_enabled: getBatchEditValue(data, 'schedule_is_enabled'),
        schedule_timezone: getBatchEditValue(data, 'schedule_timezone') || getUserTimezone(),
      });
    } else {
      resetForm();
    }
  }, [mode, data]);

  // Recompute human-readable cron description whenever cron or timezone changes
  useEffect(() => {
    if (!formData.schedule_cron) {
      setCronDescription('');
      return;
    }
    const desc = cronDescription(formData.schedule_cron);
    const abbr = getTimezoneAbbreviation(formData.schedule_timezone || 'UTC');
    setCronDescription(desc ? `${desc} (${abbr})` : desc);
  }, [formData.schedule_cron, formData.schedule_timezone]);

  const fetchTasks = async () => {
    taskService
      .getAll()
      .then(res => {
        if (res?.tasks) setTasks(res.tasks);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  /**
   * Fetches the task IDs assigned to a schedule and pre-populates the task multi-select.
   * Called in edit mode after the rest of the form state has been set from the data prop.
   * @param {number} scheduleId
   */
  const fetchAssignedTasks = async scheduleId => {
    scheduleService
      .getById(scheduleId)
      .then(res => {
        if (res?.schedules?.[0]?.task_ids) {
          setFormData(prev => ({
            ...prev,
            task_ids: res.schedules[0].task_ids.map(id => ({ value: id })),
          }));
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = (event, formPart) => {
    let name, value;

    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        if (name === 'task_ids') {
          // Multi-select: toggle { value } objects in/out of the array
          setFormData(prev => {
            const exists = prev.task_ids.find(x => x.value === value);
            return {
              ...prev,
              task_ids: exists
                ? prev.task_ids.filter(x => x.value !== value)
                : [...prev.task_ids, { value }],
            };
          });
        } else {
          setFormData({ ...formData, [name]: value });
        }
    }

    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();
    setLoading(true);

    let dataToSubmit;

    if (batchEdit) {
      let tmp = {};
      const objects = Object.entries(batchEditItems);
      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          const key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }
      dataToSubmit = data.map(item => ({
        schedule_id: item.schedule_id,
        schedule_name: item.schedule_name,
        schedule_cron: item.schedule_cron,
        schedule_is_enabled: item.schedule_is_enabled,
        ...tmp,
      }));
    } else {
      dataToSubmit = { ...formData, task_ids: formData.task_ids.map(t => t.value) };
    }

    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setFormData({ ...initialFormState });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="schedule_name"
              value={formData.schedule_name}
              onChange={handleInputChange}
              disabled={loading}
              validation={
                formErrors.schedule_name
                  ? { valid: false, message: formErrors.schedule_name }
                  : undefined
              }
            />
          </div>
        )}

        {!batchEdit && (
          <div className="form-block vertical">
            <TextareaAutosize
              name="schedule_description"
              value={formData.schedule_description}
              onChange={handleInputChange}
              disabled={loading}
              isOptional={true}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.schedule_timezone}
                name="schedule_timezone"
                label="Edit Timezone"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}
          <Select
            name="schedule_timezone"
            label="Timezone"
            value={formData.schedule_timezone}
            options={timezoneOptions}
            onChange={handleInputChange}
            disabled={loading || (batchEdit && !batchEditItems.schedule_timezone)}
            isNullable={false}
            validation={
              formErrors.schedule_timezone
                ? { valid: false, message: formErrors.schedule_timezone }
                : undefined
            }
          />
        </div>

        {!batchEdit && (
          <div className="form-block vertical">
            <Input
              type="text"
              label={true}
              inputClassName="code"
              name="schedule_cron"
              value={formData.schedule_cron}
              onChange={handleInputChange}
              disabled={loading}
              placeholder="* * * * *"
              validation={
                formErrors.schedule_cron
                  ? { valid: false, message: formErrors.schedule_cron }
                  : { valid: 'info', message: cronDescriptionText }
              }
            />
          </div>
        )}

        {!batchEdit && (
          <div className="form-block vertical">
            <Select
              name="task_ids"
              label="Tasks"
              value={formData.task_ids}
              title="Tasks assigned to this schedule"
              options={formatSelectOptions({
                options: tasks,
                optionValue: 'task_id',
                optionLabel: 'task_name',
                optionSecondaryLabel: 'task_type_name',
                tooltip: 'task_description',
              })}
              onChange={handleInputChange}
              placeholder="Select tasks..."
              disabled={loading}
              isMulti={true}
              isOptional={true}
              isNullable={true}
            />
          </div>
        )}

        <div className="form-block vertical">
          {batchEdit ? (
            <label>
              <Checkbox
                checked={batchEditItems.batch_schedule_is_enabled}
                name="batch_schedule_is_enabled"
                label="Edit Status"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          ) : null}
          <Checkbox
            name="schedule_is_enabled"
            checked={formData.schedule_is_enabled}
            onChange={handleInputChange}
            label="Enabled"
            disabled={loading || (batchEdit && !batchEditItems.batch_schedule_is_enabled)}
          />
        </div>
      </form>
    </div>
  );
};

export const AddScheduleForm = forwardRef(_AddScheduleForm);
